import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { AuthSessionService } from '../../../services/auth-session.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ApiUrl } from '../../../shared/models/apiurl.models';
import { SystemApp } from '../../../constans/SystemConfigurations';
import { LoadingMaskServiceService } from '../../../components/loading-frame/loading-frame.component';
import { LoginAction, LoginService } from '../../../components/session/login/login.service';
import { UserAds, StatusAds } from '../../../models/user-ads';
import { RouteType } from '../../create-adv/select-route/select-route.component';
import { MatDialog } from '@angular/material/dialog';
import { AdsPageComponentDialog } from '../ads/ads-page/ads-page-component-dialog.component';
import { AdsService } from '../ads/services/ads.service';

interface UserInfo {
  first_name: string;
  last_name: string;
  mainPhone: string;
  avatar: string;
  is_vip: boolean;   // ← returned by the backend on /user/user_info/
}

// SCRUM-175/176 — GET client-api/subscriptions/quota/, the same call
// vip-subscription.component.ts already uses to resolve the real plan.
// Trimmed to the fields this dashboard needs: which plan (by name) is
// active, and how much of it has been used.
interface QuotaStatus {
  is_vip: boolean;
  plan_name: string | null;
  concurrent_ads_limit: number | null;
  active_ads_count: number;
  remaining: number | null;
  quota_reached: boolean;
}

interface CategoryStat {
  key: string;
  label: string;
  icon: string;
  count: number;
  percent: number;
}

interface MonthlyCount {
  month: string;
  count: number;
}

@Component({
    selector: 'app-vip-dashboard',
    templateUrl: './vip-dashboard.component.html',
    styleUrls: ['./vip-dashboard.component.scss'],
    standalone: false
})
export class VipDashboardComponent implements OnInit, OnDestroy {
  private readonly authSession = inject(AuthSessionService);

  userInfo: UserInfo = { first_name: '', last_name: '', mainPhone: '', avatar: '', is_vip: false };
  ads: UserAds[] = [];
  recentAds: UserAds[] = [];

  /** SCRUM-267 — listing filter for the performance report. */
  get adOptions(): Array<{ id: number; name: string }> {
    return this.ads.map((a: any) => ({ id: a.id, name: a.name || ('#' + a.id) }));
  }
  loading = false;
  base_url: string;

  readonly StatusAds = StatusAds;
  readonly RouteType = RouteType;

  stats = { total: 0, active: 0, inReview: 0, paused: 0, expired: 0 };
  categoryStats: CategoryStat[] = [];
  monthlyData: MonthlyCount[] = [];
  subscriptionLevel: RouteType | null = null;
  hasSubscription = false;

  // SCRUM-175/176 — the caller's real plan name + publishing quota, from
  // GET client-api/subscriptions/quota/ (the same call
  // vip-subscription.component.ts already uses to resolve the current
  // plan/tier). Replaces the old hardcoded-LEVEL1 guess in
  // computeSubscription() and drives the quota-usage KPI tile.
  planName: string | null = null;
  quotaLoading = true;
  quotaUnlimited = false;
  quotaUsed = 0;
  quotaLimit: number | null = null;
  quotaRemaining: number | null = null;

  private destroy$ = new Subject<void>();

  private readonly TYPE_MAP: Record<string, { label: string; icon: string }> = {
    car:          { label: 'רכב',           icon: 'fa-car-alt'       },
    nadlan:       { label: 'נדל"ן',          icon: 'fa-building'      },
    sale2:        { label: 'יד שניה',        icon: 'fa-tag'           },
    required:     { label: 'דרושים',         icon: 'fa-briefcase'     },
    coworker:     { label: 'בעלי מקצוע',     icon: 'fa-tools'         },
    pet:          { label: 'חיות מחמד',      icon: 'fa-paw'           },
    teacher:      { label: 'מורים פרטיים',   icon: 'fa-graduation-cap'},
    cellular:     { label: 'סלולר',          icon: 'fa-mobile-alt'    },
    furniture:    { label: 'ריהוט',          icon: 'fa-couch'         },
    fashion:      { label: 'אופנה',          icon: 'fa-tshirt'        },
  };

  private get httpOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': this.apiKey.getJWTToken()
      })
    };
  }

  constructor(
    private http: HttpClient,
    private apiKey: ApiUrl,
    private loadingService: LoadingMaskServiceService,
    public translate: TranslateService,
    private router: Router,
    private loginService: LoginService,
    private dialog: MatDialog,
    private adsService: AdsService
  ) {
    this.base_url = SystemApp.base_url_api;
  }

  ngOnInit(): void {
    this.loadUserInfo(); // loadAds() is chained inside loadUserInfo()
    this.loadQuota();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUserInfo(): void {
    this.http.get<UserInfo>(this.apiKey.getUserInfo(), this.httpOptions)
      .pipe(takeUntil(this.destroy$), catchError(e => throwError(e.error || e)))
      .subscribe({
        next: (res) => {
          this.userInfo = res;
          // Load ads only after isVip is known — computeSubscription() depends on it
          this.loadAds();
        },
        error: () => {}
      });
  }

  // SCRUM-175/176 — resolves the real plan name + usage, independently
  // of loadUserInfo()/loadAds(); a failed/slow quota lookup must not block
  // the ad list from rendering.
  private loadQuota(): void {
    this.http.get<QuotaStatus>(this.apiKey.getQuotaUrl(), this.httpOptions)
      .pipe(takeUntil(this.destroy$), catchError(e => throwError(e.error || e)))
      .subscribe({
        next: (res) => {
          this.planName = res?.is_vip ? (res.plan_name ?? null) : null;
          this.quotaUnlimited = res?.is_vip === true && res.concurrent_ads_limit == null;
          this.quotaUsed = res?.active_ads_count ?? 0;
          this.quotaLimit = res?.concurrent_ads_limit ?? null;
          this.quotaRemaining = res?.remaining ?? null;
          this.quotaLoading = false;
        },
        error: () => { this.quotaLoading = false; }
      });
  }

  loadAds(): void {
    this.loading = true;
    this.loadingService.setOnLoadingMask(true, true);
    this.http.get<UserAds[]>(this.apiKey.getAds(), this.httpOptions)
      .pipe(takeUntil(this.destroy$), catchError(e => throwError(e.error || e)))
      .subscribe({
        next: res => {
          this.ads = res || [];
          this.computeSubscription();
          this.buildStats();
          this.buildCategoryStats();
          this.buildMonthlyData();
          this.recentAds = [...this.ads]
            .sort((a, b) => new Date(b.createTime).getTime() - new Date(a.createTime).getTime())
            .slice(0, 5);
          this.loading = false;
          this.loadingService.setOnLoadingMask(false, false);
        },
        error: () => {
          this.loading = false;
          this.loadingService.setOnLoadingMask(false, false);
        }
      });
  }

  private computeSubscription(): void {
    // Single source of truth: the isVip flag returned by /user/user_info/
    this.hasSubscription = this.userInfo.is_vip === true;
    // SCRUM-175/176 — the real plan name/tier comes from loadQuota()
    // (client-api/subscriptions/quota/) instead of a hardcoded LEVEL1 guess.
  }

  private buildStats(): void {
    this.stats = {
      total:    this.ads.length,
      active:   this.ads.filter(a => a.statusAds === StatusAds.Active || a.statusAds === StatusAds.Done).length,
      inReview: this.ads.filter(a => a.statusAds === StatusAds.inReview || a.statusAds === StatusAds.PayPending).length,
      paused:   this.ads.filter(a => a.statusAds === StatusAds.Pause).length,
      expired:  this.ads.filter(a => a.statusAds === StatusAds.Delete || a.statusAds === StatusAds.Blocked).length,
    };
  }

  private buildCategoryStats(): void {
    const counts: Record<string, number> = {};
    this.ads.forEach(a => {
      const key = (a.resourcetype || 'other').toLowerCase();
      counts[key] = (counts[key] || 0) + 1;
    });
    const total = this.ads.length || 1;
    this.categoryStats = Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([key, count]) => ({
        key,
        label: this.TYPE_MAP[key]?.label ?? key,
        icon:  this.TYPE_MAP[key]?.icon  ?? 'fa-tag',
        count,
        percent: Math.round((count / total) * 100)
      }));
  }

  private buildMonthlyData(): void {
    const map: Record<string, number> = {};
    this.ads.forEach(a => {
      if (!a.createTime) return;
      const d = new Date(a.createTime);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      map[key] = (map[key] || 0) + 1;
    });
    this.monthlyData = Object.entries(map)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-6)
      .map(([month, count]) => ({ month: this.formatMonth(month), count }));
  }

  private formatMonth(ym: string): string {
    const [year, month] = ym.split('-');
    const names = ['ינו', 'פבר', 'מרץ', 'אפר', 'מאי', 'יונ', 'יול', 'אוג', 'ספט', 'אוק', 'נוב', 'דצמ'];
    return `${names[parseInt(month, 10) - 1]} ${year.slice(2)}`;
  }

  get maxMonthlyCount(): number {
    return Math.max(...this.monthlyData.map(m => m.count), 1);
  }

  // SCRUM-175/176 — tierMeta() reads the real plan_name from
  // client-api/subscriptions/quota/ (set by loadQuota()) instead of the
  // old hardcoded-LEVEL1 guess, which always showed "Go" for every VIP.
  private tierMeta(): { label: string; cls: string } {
    const name = (this.planName || '').trim().toLowerCase();
    if (name === 'pro')  return { label: 'Pro',  cls: 'tier-pro'  };
    if (name === 'plus') return { label: 'Plus', cls: 'tier-plus' };
    if (name === 'go')   return { label: 'Go',   cls: 'tier-go'   };
    // Unrecognized/new plan name — show it as-is rather than
    // silently mislabeling it as "Go".
    return { label: this.planName || 'מנוי פעיל', cls: 'tier-go' };
  }

  subscriptionLabel(): string {
    if (!this.hasSubscription) return 'ללא מנוי';
    return this.tierMeta().label;
  }

  subscriptionClass(): string {
    if (!this.hasSubscription) return 'tier-none';
    return this.tierMeta().cls;
  }

  // SCRUM-175 — short usage string for the quota KPI tile:
  // "3/10", "ללא הגבלה" when unlimited, or "…" while loading.
  quotaLabel(): string {
    if (this.quotaLoading) return '…';
    if (this.quotaUnlimited) return 'ללא הגבלה';
    if (this.quotaLimit == null) return `${this.quotaUsed}`;
    return `${this.quotaUsed}/${this.quotaLimit}`;
  }

  // SCRUM-175 — percent of the quota used, for the sub-line under
  // quotaLabel(). 0 while loading/unlimited/no limit — quotaDetail()
  // decides separately whether that sub-line renders at all.
  quotaPercent(): number {
    if (this.quotaUnlimited || !this.quotaLimit) return 0;
    return Math.min(100, Math.round((this.quotaUsed / this.quotaLimit) * 100));
  }

  // SCRUM-175 — "נותרו 7 · 30% נוצל", using the backend's own `remaining`
  // rather than recomputing limit - used here. Empty (hidden in the
  // template) while loading or on an unlimited/no-limit plan, where a
  // percentage wouldn't mean anything.
  quotaDetail(): string {
    if (this.quotaLoading || this.quotaUnlimited || this.quotaLimit == null) return '';
    const remaining = this.quotaRemaining ?? Math.max(0, this.quotaLimit - this.quotaUsed);
    return `נותרו ${remaining} · ${this.quotaPercent()}% נוצל`;
  }

  statusLabel(s: StatusAds): string {
    const m: Partial<Record<StatusAds, string>> = {
      [StatusAds.Active]:     'פעיל',
      [StatusAds.Done]:       'הושלם',
      [StatusAds.Delete]:     'נמחק',
      [StatusAds.inReview]:   'בבדיקה',
      [StatusAds.Pause]:      'מושהה',
      [StatusAds.PayPending]: 'ממתין לתשלום',
      [StatusAds.Blocked]:    'חסום',
      [StatusAds.NeedsFix]:   'ממתין לתיקון',
      [StatusAds.Cancelled]:  'בוטל',
    };
    return m[s] ?? s;
  }

  statusClass(s: StatusAds): string {
    if (s === StatusAds.Active || s === StatusAds.Done)                                   return 'badge-active';
    if (s === StatusAds.inReview || s === StatusAds.Pause || s === StatusAds.PayPending)  return 'badge-pending';
    return 'badge-inactive';
  }

  imgUrl(ad: UserAds): string {
    if (ad.images?.length) {
      const img = ad.images[0].image;
      return img.startsWith('http') ? img : this.base_url + img;
    }
    return 'assets/imgs/placeholder.png';
  }

  timeAgo(dateStr: string): string {
    if (!dateStr) return '';
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (diff < 60)       return 'עכשיו';
    if (diff < 3600)     return `לפני ${Math.floor(diff / 60)} דק'`;
    if (diff < 86400)    return `לפני ${Math.floor(diff / 3600)} שע'`;
    if (diff < 2592000)  return `לפני ${Math.floor(diff / 86400)} ימים`;
    if (diff < 31536000) return `לפני ${Math.floor(diff / 2592000)} חודשים`;
    return `לפני ${Math.floor(diff / 31536000)} שנים`;
  }

  openAdDialog(ad: UserAds): void {
    const ref = this.dialog.open(AdsPageComponentDialog, {
      data: { selectAds: ad, namespace: ad.resourcetype || 'Car' },
      width: '90vw', maxWidth: '1100px',
      panelClass: 'ad-detail-dialog', direction: 'rtl'
    });
    ref.afterClosed().subscribe((updated: UserAds | undefined) => {
      if (updated) {
        this.ads = this.ads.map(a => a['id'] === updated['id'] ? updated : a);
        this.buildStats();
        this.buildCategoryStats();
        this.recentAds = [...this.ads]
          .sort((a, b) => new Date(b.createTime).getTime() - new Date(a.createTime).getTime())
          .slice(0, 5);
      }
    });
  }

  logout(): void {
    // POD1-7: full logout - revoke the refresh token server-side and drop
    // the socket, not just clear the local flag.
    this.authSession.logout().subscribe();
    this.loginService.sendClickEvent(LoginAction.Close);
  }
}
